import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Trello,
  Rocket,
  FolderKanban,
  ClipboardList,
  Users,
  TrendingUp,
  BellRing,
  History,
  LogOut,
  Zap,
  CalendarRange,
  Bell,
  Archive,
  ChevronDown,
  ListOrdered,
  LayoutGrid,
  Building2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/UI";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", exact: true },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects-link", adminOnly: true },
  { to: "/timeline", label: "Timeline", icon: CalendarRange, testid: "nav-timeline-link" },
  { to: "/backlog", label: "Backlog", icon: ClipboardList, testid: "nav-backlog-link" },
  { to: "/kanban", label: "Kanban", icon: Trello, testid: "nav-kanban-link" },
  {
    label: "Sprint Board",
    icon: Rocket,
    adminOnly: true,
    children: [
      { to: "/sprint", label: "Sprint Team", icon: LayoutGrid, testid: "nav-sprint-link" },
      { to: "/sprints", label: "List Sprint", icon: ListOrdered, testid: "nav-sprints-link" },
    ],
  },
  {
    label: "Laporan",
    icon: TrendingUp,
    adminOnly: true,
    children: [
      { to: "/performance/company", label: "Laporan Perusahaan", icon: Building2, testid: "nav-laporan-company-link" },
      { to: "/performance/team", label: "Laporan Tim", icon: Users, testid: "nav-laporan-team-link" },
    ],
  },
  { to: "/performance", label: "Laporan", icon: TrendingUp, testid: "nav-performance-link", employeeOnly: true },
  { to: "/approvals", label: "Approvals", icon: BellRing, testid: "nav-approvals-link", adminOnly: true },
  { to: "/archive", label: "Arsip", icon: Archive, testid: "nav-archive-link" },
  { to: "/audit", label: "Audit Trail", icon: History, testid: "nav-audit-link", adminOnly: true, topbar: true },
  { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees-link", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-transparent text-slate-100">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-white/10 bg-[#0F172A] lg:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F59E0B] text-[#0F172A] shadow-sm shadow-[#F59E0B]/30">
            <Zap size={20} strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-heading text-lg font-bold leading-none tracking-tight">SuperProduction</p>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-[#94A3B8]">Track Everything</p>
          </div>
        </div>

        <p className="px-6 pb-2 pt-4 text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Menu</p>
        <nav className="flex-1 space-y-0.5 px-4">
          {NAV.filter((n) => !n.topbar && (!n.adminOnly || isAdmin) && (!n.employeeOnly || !isAdmin)).map((n) =>
            n.children ? (
              <NavGroup key={n.label} item={n} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-md border-l-2 px-3 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]"
                      : "border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                        isActive ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-white/5 text-slate-500 group-hover:text-slate-300"
                      }`}
                    >
                      <n.icon size={16} />
                    </span>
                    {n.label}
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="border-t border-white/10 p-4">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-slate-400 hover:bg-white/5 hover:text-[#E2E8F0]"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white/5">
              <LogOut size={16} />
            </span>
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col lg:ml-72">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-[#0F172A] px-4 py-3.5 lg:px-8">
          <div className="lg:hidden">
            <span className="font-heading text-lg font-bold">SuperProduction</span>
          </div>
          <div className="flex flex-1 items-center justify-end gap-3">
            {isAdmin && (
              <button
                onClick={() => navigate("/audit")}
                className="rounded-md p-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200"
                title="Audit Trail"
                data-testid="topbar-audit-link"
              >
                <History size={19} />
              </button>
            )}
            <NotificationBell user={user} isAdmin={isAdmin} />
            <span
              className={`rounded-sm px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                isAdmin ? "border border-[#F59E0B]/40 bg-[#F59E0B]/15 text-[#F59E0B]" : "border border-[#64748B]/40 bg-[#64748B]/15 text-[#64748B]"
              }`}
              data-testid="role-badge"
            >
              {isAdmin ? "Admin / PM" : "Employee"}
            </span>
            <div onClick={() => navigate("/profile")} className="cursor-pointer" data-testid="topbar-avatar">
              <Avatar src={user?.avatar_url} name={user?.name} size={32} />
            </div>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="flex gap-1 overflow-x-auto border-b border-white/10 bg-[#0F172A] px-2 py-2 lg:hidden">
          {NAV.filter((n) => !n.topbar && (!n.adminOnly || isAdmin) && (!n.employeeOnly || !isAdmin))
            .flatMap((n) => (n.children ? n.children.map((c) => ({ ...c, icon: c.icon || n.icon })) : [n]))
            .map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "text-slate-400"
                }`
              }
            >
              <n.icon size={15} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}

function NavGroup({ item }) {
  const location = useLocation();
  const active = item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));
  const [open, setOpen] = useState(active);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`group flex w-full items-center gap-3 rounded-md border-l-2 px-3 py-2.5 text-sm font-medium transition-all ${
          active ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-100"
        }`}
      >
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
            active ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-white/5 text-slate-500 group-hover:text-slate-300"
          }`}
        >
          <item.icon size={16} />
        </span>
        {item.label}
        <ChevronDown size={14} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-6 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-all ${
                  isActive ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
                }`
              }
            >
              {c.icon && <c.icon size={14} />}
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationBell({ user, isAdmin }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const seenKey = `notif_seen_${user?.id || "anon"}`;

  const load = () => {
    api.get("/notifications").then((r) => setItems(r.data.items || [])).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        markSeen();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [seenAt, setSeenAt] = useState(() => localStorage.getItem(seenKey) || "");
  const unread = items.filter((i) => (i.at || "") > seenAt);

  const markSeen = () => {
    if (items.length === 0) return;
    const newest = items.reduce((max, i) => ((i.at || "") > max ? i.at : max), "");
    localStorage.setItem(seenKey, newest);
    setSeenAt(newest);
  };

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (!next) markSeen();
      return next;
    });
  };

  const goItem = (item) => {
    markSeen();
    setOpen(false);
    navigate(item.type === "approval" ? "/approvals" : "/kanban");
  };

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        data-testid="notif-bell"
        className="relative flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-[#0F172A] text-slate-300 transition-colors hover:text-white"
      >
        <Bell size={16} />
        {unread.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-sm bg-[#F59E0B] px-1 text-[9px] font-bold text-[#0F172A]" data-testid="notif-badge">
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-md border border-white/10 bg-[#0F172A] shadow-xl shadow-black/50" data-testid="notif-panel">
          <div className="border-b border-white/10 bg-[#020617] px-4 py-2.5">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-300">
              {isAdmin ? "Task menunggu review" : "Task untuk kamu"}
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-xs text-slate-500">Tidak ada notifikasi</p>
            ) : (
              items.map((i) => {
                const isUnread = (i.at || "") > seenAt;
                return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => goItem(i)}
                  className={`flex w-full flex-col gap-0.5 border-b border-white/5 px-4 py-2.5 text-left hover:bg-white/5 ${isUnread ? "bg-amber-500/[0.07]" : ""}`}
                >
                  <span className="flex items-center gap-1.5">
                    {isUnread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />}
                    <span className="truncate text-sm font-medium text-slate-100">{i.title}</span>
                  </span>
                  <span className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>{i.message}</span>
                    <span>{fmt(i.at)}</span>
                  </span>
                </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
